import { useCallback, useEffect, useReducer, useState } from 'react';
import moment from 'moment';
import { Observable, zip } from 'rxjs';
import { take } from 'rxjs/operators';
import { Input } from '@glu/form-components';

import Table from '../placeResolver/Table';
import Spinner from '../spinner/Spinner';

export const variants = {
  input: Input,
  spinner: Spinner,
  table: Table
};

interface ResolverState<T> {
  lastSync?: moment.Moment;
  results: T;
}

type PlaceResolverArgs = {
  services: Array<Observable<any>>;
  variant: any;
};

type PlaceResolverReturnParams<T> = {
  Placeholder: any;
  results: T;
  lastSync?: moment.Moment;
  rerun: () => void;
};

const usePlaceResolver = <T>({
  services,
  variant
}: PlaceResolverArgs): PlaceResolverReturnParams<T> => {
  const Placeholder = variant || variants.spinner;
  const [hasPlaceholder, setHasPlaceholder] = useState(true);
  const [{ lastSync, results }, setResolverState] = useState<ResolverState<T>>({
    results: [] as unknown as T
  });
  const [run, rerun] = useReducer((rerunFlag) => !rerunFlag, true);

  useEffect(() => {
    if (services != null) {
      setHasPlaceholder(true);
      const service$ = zip(...services)
        .pipe(take(1))
        .subscribe((data: unknown) => {
          setResolverState({
            lastSync: moment(),
            results: data as T
          });
          setHasPlaceholder(false);
        });
      return () => service$.unsubscribe();
    }
  }, [run, services]);

  const rerunner = useCallback(() => {
    setHasPlaceholder(true);
    rerun();
  }, []);

  return {
    Placeholder: hasPlaceholder && services ? Placeholder : null,
    results,
    lastSync,
    rerun: rerunner
  };
};

export default usePlaceResolver;
